import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Heart, AlertTriangle, Phone, HelpCircle, Shield, Clock, Users } from 'lucide-react';

export default function ResponsibleGaming() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="w-10 h-10 text-green-400" />
              <div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">
                  Responsible Gaming
                </h1>
                <p className="text-muted-foreground">
                  Your well-being matters to us
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="prose prose-invert max-w-none space-y-8">
            
            {/* Important Notice */}
            <section className="bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-yellow-400 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Important Notice
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  <strong className="text-foreground">SportsAnalysisAB is NOT a gambling or betting platform.</strong> 
                  We provide statistical analysis and probability estimates for entertainment and educational purposes only. 
                  We do not accept, facilitate, or process any wagers.
                </p>
                <p>
                  However, we recognize that our content may be of interest to sports fans who also engage in 
                  legal sports betting through licensed providers. We take responsible gaming seriously and 
                  provide the following resources for anyone who may need support.
                </p>
              </div>
            </section>

            {/* Our Commitment */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" />
                Our Commitment
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>SportsAnalysisAB is committed to promoting responsible attitudes toward sports analysis and gaming:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">18+ Only:</strong> Our platform is strictly for adults aged 18 and over</li>
                  <li><strong className="text-foreground">Entertainment Focus:</strong> Our content is for entertainment and educational purposes</li>
                  <li><strong className="text-foreground">No Betting Advice:</strong> We never provide betting tips or gambling recommendations</li>
                  <li><strong className="text-foreground">Transparent Statistics:</strong> We clearly display our prediction accuracy so users can make informed decisions</li>
                  <li><strong className="text-foreground">Support Resources:</strong> We provide access to help resources for those who need them</li>
                </ul>
              </div>
            </section>

            {/* Warning Signs */}
            <section className="bg-red-500/10 border border-red-500/20 rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-red-400 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Warning Signs of Problem Gambling
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>If you or someone you know engages in sports betting, be aware of these warning signs:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Spending more money or time gambling than intended</li>
                  <li>Chasing losses by betting more to recover money</li>
                  <li>Lying to friends or family about gambling habits</li>
                  <li>Borrowing money or selling possessions to gamble</li>
                  <li>Neglecting work, school, or family responsibilities</li>
                  <li>Feeling restless or irritable when trying to stop gambling</li>
                  <li>Gambling to escape problems or relieve negative feelings</li>
                  <li>Needing to bet larger amounts for the same excitement</li>
                </ul>
              </div>
            </section>

            {/* Tips for Responsible Gaming */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-green-400" />
                Tips for Responsible Gaming
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>If you choose to participate in legal sports betting:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Set a Budget:</strong> Only bet what you can afford to lose</li>
                  <li><strong className="text-foreground">Set Time Limits:</strong> Don't let gambling consume excessive time</li>
                  <li><strong className="text-foreground">Don't Chase Losses:</strong> Accept losses as part of the experience</li>
                  <li><strong className="text-foreground">Stay Informed:</strong> Understand the odds and that the house always has an edge</li>
                  <li><strong className="text-foreground">Keep Balance:</strong> Maintain other hobbies and social activities</li>
                  <li><strong className="text-foreground">Never Borrow:</strong> Don't borrow money to gamble</li>
                  <li><strong className="text-foreground">Take Breaks:</strong> Regular breaks help maintain perspective</li>
                  <li><strong className="text-foreground">Avoid Alcohol:</strong> Don't gamble while intoxicated</li>
                </ul>
              </div>
            </section>

            {/* Help Resources */}
            <section className="bg-green-500/10 border border-green-500/20 rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-green-400 mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Help Resources
              </h2>
              <div className="text-muted-foreground space-y-6 text-sm">
                <p>
                  If you or someone you know is struggling with gambling, help is available 24/7:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-background/50 rounded-sm p-4">
                    <h3 className="font-bold text-foreground mb-2">Alberta Gambling Helpline</h3>
                    <p>Phone: <a href="tel:1-866-332-2322" className="text-green-400 hover:underline font-mono">1-866-332-2322</a></p>
                    <p>Available 24 hours a day, 7 days a week</p>
                    <p>Website: <a href="https://www.aglc.ca/responsible-gambling" className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">AGLC Responsible Gambling</a></p>
                  </div>
                  
                  <div className="bg-background/50 rounded-sm p-4">
                    <h3 className="font-bold text-foreground mb-2">Alberta Health Services - Addiction Helpline</h3>
                    <p>Phone: <a href="tel:1-866-332-2322" className="text-green-400 hover:underline font-mono">1-866-332-2322</a></p>
                    <p>Website: <a href="https://www.albertahealthservices.ca/amh/Page16970.aspx" className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">AHS Gambling Support</a></p>
                  </div>
                  
                  <div className="bg-background/50 rounded-sm p-4">
                    <h3 className="font-bold text-foreground mb-2">Gamblers Anonymous</h3>
                    <p>Website: <a href="https://www.gamblersanonymous.org" className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">www.gamblersanonymous.org</a></p>
                    <p>Find meetings in your area</p>
                  </div>
                  
                  <div className="bg-background/50 rounded-sm p-4">
                    <h3 className="font-bold text-foreground mb-2">Canadian Centre on Substance Use and Addiction</h3>
                    <p>Website: <a href="https://www.ccsa.ca" className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">www.ccsa.ca</a></p>
                  </div>
                  
                  <div className="bg-background/50 rounded-sm p-4">
                    <h3 className="font-bold text-foreground mb-2">Crisis Services Canada</h3>
                    <p>Phone: <a href="tel:1-833-456-4566" className="text-green-400 hover:underline font-mono">1-833-456-4566</a></p>
                    <p>Text: 45645 (4pm-12am ET)</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Self-Exclusion */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-400" />
                Self-Exclusion Programs
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  If you need a break from gambling, self-exclusion programs are available:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    <strong className="text-foreground">GameSense (Alberta):</strong> Alberta Gaming, Liquor and Cannabis (AGLC) 
                    offers voluntary self-exclusion from casinos, racetracks, and VLT outlets
                  </li>
                  <li>
                    <strong className="text-foreground">Online Betting Sites:</strong> Most licensed online betting platforms 
                    offer self-exclusion and deposit limit tools
                  </li>
                </ul>
                <p className="mt-4">
                  For more information about self-exclusion in Alberta, visit 
                  <a href="https://aglc.ca/responsible-gambling/self-exclusion" className="text-blue-400 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                    AGLC Self-Exclusion Program
                  </a>
                </p>
              </div>
            </section>

            {/* For Friends & Family */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                For Friends and Family
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  If you're concerned about someone's gambling habits:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Express your concerns calmly and without judgment</li>
                  <li>Encourage them to seek help from a professional</li>
                  <li>Avoid lending money or paying their gambling debts</li>
                  <li>Take care of your own mental health and well-being</li>
                  <li>Contact support services for guidance on how to help</li>
                </ul>
                <p className="mt-4">
                  <strong className="text-foreground">Gam-Anon</strong> offers support for family members and friends of problem gamblers: 
                  <a href="https://www.gam-anon.org" className="text-blue-400 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                    www.gam-anon.org
                  </a>
                </p>
              </div>
            </section>

            {/* Reminder */}
            <section className="bg-card border border-border rounded-sm p-6 text-center">
              <p className="text-muted-foreground text-sm mb-4">
                Remember: SportsAnalysisAB is an analytics and entertainment platform. 
                We do not facilitate gambling of any kind.
              </p>
              <p className="text-lg font-display font-bold text-foreground">
                If gambling stops being fun, it's time to stop.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Help is always available. You don't have to face this alone.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
